/**
 * Private aviation, in every city: a terminal on the edge of the airport with a helicopter, a jet and a skydiving
 * plane on the apron. A helicopter tour over where you are (or, once you own one, free, and a hop to anywhere in the
 * country); a skydive (a tandem jump from 4,000 m, landing on your own street); and, for the very rich, buying the
 * helicopter or the jet. Your jet flies you abroad when you like: no ticket, no schedule, fuel and crew only (the
 * visa still matters). Prices are dollar prices: this is the same everywhere.
 */
import { cheaper } from './prices.ts';
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { AIRPORT_INFO, USD_NGN, abroad, homeCountry, departureAirport, destinationOf, routeMinutes, travelOf, visaValid, type Country } from './travel.ts';
import { hereCountry } from './character.ts';
import { socialOf } from './social.ts';

export const AIRCRAFT = {
  heli: { id: 'heli', name: 'Aerion H5', kind: 'helicopter', price: cheaper(2_100_000 * USD_NGN, 1_000_000), note: 'Five seats, a glass cockpit, a pilot who calls you "sir" or "madam". From your roof to anywhere in the city in minutes.' },
  jet: { id: 'jet', name: 'Skylark 650', kind: 'private jet', price: cheaper(21_000_000 * USD_NGN, 1_000_000), note: 'Twelve leather seats, a bed at the back, a crew of three. Any city, any time, no queue.' },
} as const;
export type AircraftId = keyof typeof AIRCRAFT;
export const owns = (s: LifeState, a: AircraftId) => s.owned.includes(`aircraft:${a}`);
/**
 * Your helicopter where you are: one per country (a player: "I bought a heli in Nigeria, I can't buy one at my house in
 * the US?"). The one in your home country is `aircraft:heli` (as it always was); one bought abroad is
 * `aircraft:heli@<country>`, kept at that country's terminal and on your pad at your house there.
 */
export const heliKeyHere = (s: LifeState) => { const c = hereCountry(s); return c === homeCountry(s) ? 'aircraft:heli' : `aircraft:heli@${c}`; };
export const ownsHeliHere = (s: LifeState) => s.owned.includes(heliKeyHere(s));
/** a helicopter in the country your house abroad is in (its pad) */
export const ownsHeliIn = (s: LifeState, c: Country) => s.owned.includes(c === homeCountry(s) ? 'aircraft:heli' : `aircraft:heli@${c}`);

export const heliTourPrice = () => cheaper(450 * USD_NGN, 1_000);
export const skydivePrice = () => cheaper(380 * USD_NGN, 1_000);
/** what an hour of your jet costs you (fuel, crew, the airport) */
export const jetHour = () => cheaper(4_200 * USD_NGN, 1_000);

/** The private terminal here (a made-up operator name over a real airport). */
export function aviationName(s: LifeState): string {
  const c = hereCountry(s), w = abroad(s);
  return ({ nigeria: 'Skyline Private Aviation, Lagos', france: 'Aéro Privé, Le Bourget', usa: w === 'sf' ? 'Bayshore Private Aviation, SFO' : w === 'chicago' ? 'Lakeside Private Aviation, Midway' : 'Hudson Private Aviation, Teterboro', china: 'Golden Wing Business Aviation, Hongqiao', japan: 'Sora Private Aviation, Haneda', italy: 'Toscana Private Aviation, Peretola', uae: 'Falcon Private Aviation, Al Maktoum' } as Record<Country, string>)[c];
}

export function buyAircraft(s: LifeState, a: AircraftId): { ok: boolean; line: string } {
  const m = AIRCRAFT[a], key = a === 'heli' ? heliKeyHere(s) : `aircraft:${a}`;
  if (s.owned.includes(key)) return { ok: false, line: a === 'heli' && key !== 'aircraft:heli' ? `You already have a ${m.name} here.` : `You already have the ${m.name}.` };
  if (!spend(s, m.price, `${m.name} (${m.kind})`)) return { ok: false, line: `The ${m.name} is out of reach for now.` };
  s.owned.push(key);
  s.history.push({ t: s.minutes, text: `Bought a ${m.kind}: the ${m.name}.` });
  socialOf(s).followers += a === 'jet' ? 2_000 : 600;
  return { ok: true, line: a === 'jet' ? `The ${m.name} is yours: your name on the manifest, your crew waiting on the apron. Fly it from any private terminal.` : `The ${m.name} is yours. The pilot hands you the headset: "Where to?"` };
}

/** A tour over the city: paid (or free in your own helicopter). */
export function heliTour(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!ownsHeliHere(s) && !spend(s, heliTourPrice(), 'Helicopter tour')) return { ok: false, line: "You can't afford the tour.", events: [] };
  s.needs.fun = Math.min(100, s.needs.fun + 16);
  s.history.push({ t: s.minutes, text: 'Flew over the city in a helicopter.' });
  return { ok: true, line: '', events: advance(s, 30, { busy: true }) };
}

/** A tandem skydive: out of the plane at 4,000 m, the city underneath, the canopy, and down onto the street. */
export function skydive(s: LifeState): { ok: boolean; line: string; events: LifeEvent[] } {
  if (!spend(s, skydivePrice(), 'Skydive (tandem)')) return { ok: false, line: "You can't afford the jump.", events: [] };
  const st = s.stats as LifeState['stats'] & { skydives?: number };
  st.skydives = (st.skydives ?? 0) + 1;
  s.needs.fun = Math.min(100, s.needs.fun + 25);
  s.needs.energy = Math.max(0, s.needs.energy - 6);
  if (st.skydives === 1) s.history.push({ t: s.minutes, text: 'Jumped out of a plane at 4,000 metres (and lived).' });
  return { ok: true, line: '', events: advance(s, 45, { busy: true }) };
}

/** Your jet abroad: a trip like any other (the visa still counts), only you leave when you like and pay for the fuel. */
export function jetOut(s: LifeState, country: Country): { ok: boolean; line: string } {
  if (!owns(s, 'jet')) return { ok: false, line: 'You need a jet.' };
  const t = travelOf(s), d = destinationOf(s, country);
  if (t.trip) return { ok: false, line: 'You’re already on a trip.' };
  if (!visaValid(s, d.country)) return { ok: false, line: `You need a valid ${d.visa} first (Phone → Travel).` };
  const cost = Math.round((routeMinutes(d.homeAirport, d.awayAirport) / 60) * jetHour());
  if (!spend(s, cost, `Your jet: ${AIRPORT_INFO[d.homeAirport].city} → ${d.city} (fuel and crew)`)) return { ok: false, line: 'Not enough for the fuel.' };
  t.trip = { country: d.country, cabin: 'business', stage: 'booked', where: d.firstStop, arrived: 0, nights: 0, seen: [], from: d.homeAirport, to: d.awayAirport, fare: 0, jet: true };
  return { ok: true, line: `Wheels up whenever you are. ${AIRPORT_INFO[d.awayAirport].city}: about ${Math.round(routeMinutes(d.homeAirport, d.awayAirport) / 60)} hours.` };
}
/** Home on your jet, from wherever you are abroad. */
export function jetHome(s: LifeState): { ok: boolean; line: string } {
  const t = travelOf(s);
  if (!owns(s, 'jet') || !t.trip || t.trip.stage !== 'abroad') return { ok: false, line: 'You’re not abroad.' };
  const from = departureAirport(s), cost = Math.round((routeMinutes(from, t.trip.from) / 60) * jetHour());
  if (!spend(s, cost, `Your jet home (fuel and crew)`)) return { ok: false, line: 'Not enough for the fuel.' };
  t.trip.jet = true;
  return { ok: true, line: 'The crew have the steps down. Home.' };
}
export const jetCost = (s: LifeState, country: Country) => { const d = destinationOf(s, country); return Math.round((routeMinutes(d.homeAirport, d.awayAirport) / 60) * jetHour()); };
